/* Authoring a contract — the one act this module exists for, and the one it had no control for.
 *
 * `POST /v1/contracts` has been specified and reachable since the module shipped and no screen called
 * it, so a promise between two parties could be signed, activated, evaluated and retired through the
 * UI and only ever *written* by curl. That is the "green does not mean wired" gap at the top of the
 * lifecycle: every later step had a button and the first had none.
 *
 * **A drawer rather than a route or a modal, and the reason is what a reader needs beside them.** A
 * new contract is written *against* the ledger it joins — which datasets already carry promises, who
 * the counterparties tend to be, what severity the neighbours chose — and a full-page route takes all
 * of that away at the moment it is most useful. It is also five fields plus three optional term
 * blocks: too much for a modal, and a wizard for five fields is ceremony. The kit's `Drawer` brings
 * Escape, focus restoration and a labelled close, which the reference's hand-rolled panel cannot.
 *
 * **The terms carry their own absence rule, at the point of choosing rather than in a footnote.**
 * Every term treats a missing fact as a breach, and that is the single most counter-intuitive rule in
 * the module: a dataset never profiled does not satisfy a score floor by having no score. Somebody
 * ticking a box is deciding to promise something, and the consequence belongs there.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useDatasetNames } from '@/features/catalog-browser';
import type { Contract } from '@/shared/api/generated/contracts';
import { Alert } from '@/shared/ui/Alert';
import { CheckboxField, SelectField, TextField } from '@/shared/ui/Field';
import { Drawer } from '@/shared/ui/Drawer';

import { useCreateContract } from '../api/contracts';
import { checkDraft, EMPTY_DRAFT, SEVERITIES, TERM_KINDS } from '../model/contracts';
import type { AgeUnit, DraftForm, TermKind } from '../model/contracts';

export interface NewContractDrawerProps {
  readonly onClose: () => void;
  /** Handed the created draft, so the ledger can say what happened where the form no longer is. */
  readonly onCreated: (contract: Contract) => void;
}

/** Which two form fields each term block owns, as functions rather than as key names.
 *
 * **Key names would type-check while being wrong.** A computed `{ [field.use]: checked }` resolves
 * to a union of every key in the form, so pairing `useScore` with `name` — or writing a boolean into
 * `age` — compiles cleanly and fails silently at runtime. Reading and writing through typed closures
 * makes a mismatched pair a compile error, which is what this map exists to prevent: a fourth term is
 * one entry here and cannot half-exist.
 */
const TERM_FIELD: Record<
  TermKind,
  {
    readonly on: (form: DraftForm) => boolean;
    readonly toggle: (checked: boolean) => Partial<DraftForm>;
    readonly value: (form: DraftForm) => string;
    readonly set: (value: string) => Partial<DraftForm>;
  }
> = {
  score: {
    on: (form) => form.useScore,
    toggle: (useScore) => ({ useScore }),
    value: (form) => form.score,
    set: (score) => ({ score }),
  },
  assessment: {
    on: (form) => form.useAge,
    toggle: (useAge) => ({ useAge }),
    value: (form) => form.age,
    set: (age) => ({ age }),
  },
  schema: {
    on: (form) => form.useSchema,
    toggle: (useSchema) => ({ useSchema }),
    value: (form) => form.schema,
    set: (schema) => ({ schema }),
  },
};

export function NewContractDrawer({
  onClose,
  onCreated,
}: NewContractDrawerProps): ReactElement {
  const { t } = useTranslation();
  const create = useCreateContract();
  const names = useDatasetNames();
  const [form, setForm] = useState<DraftForm>(EMPTY_DRAFT);
  /* Held rather than derived, so the drawer is quiet until somebody submits. A form that turns red
   * as you fill it in is a form telling you that you are wrong before you have finished. */
  const [problem, setProblem] = useState<string | null>(null);

  const patch = (change: Partial<DraftForm>): void => {
    setForm((current) => ({ ...current, ...change }));
    setProblem(null);
  };

  /* Names, never ids. `dataset_id` is a UUID and asking somebody to paste one is asking them to make
   * a mistake nothing downstream can catch — the contract would be written against whatever table
   * that id happens to name. Sorted by the name a reader recognises rather than by id. */
  const datasets = [...(names.data?.byId ?? new Map<string, string>())]
    .map(([id, name]) => ({ value: id, label: name }))
    .sort((left, right) => left.label.localeCompare(right.label));

  const submit = (): void => {
    const checked = checkDraft(form);
    if (!checked.ok) {
      setProblem(checked.problem);
      return;
    }
    setProblem(null);
    create.mutate(
      { body: checked.body },
      {
        onSuccess: (contract) => {
          onCreated(contract);
          onClose();
        },
      },
    );
  };

  /* The same-party refusal shows while typing, alone among the checks, because it is the only one a
   * reader cannot see for themselves: two fields side by side holding the same string look correct
   * until the server refuses them. */
  const sameParty =
    form.producer.trim() !== '' && form.producer.trim() === form.consumer.trim();

  return (
    <Drawer
      title={t('contracts.new.title')}
      closeLabel={t('contracts.new.close')}
      onClose={onClose}
      meta={<span className="lq-ct__newLede">{t('contracts.new.lede')}</span>}
    >
      <SelectField
        label={t('contracts.new.dataset')}
        value={form.datasetId}
        onChange={(datasetId) => {
          patch({ datasetId });
        }}
        options={[
          { value: '', label: t('contracts.new.datasetPlaceholder') },
          ...datasets,
        ]}
        hint={
          names.isError
            ? t('contracts.new.datasetUnread')
            : names.data?.truncated === true
              ? t('contracts.new.datasetTruncated')
              : undefined
        }
      />

      <TextField
        label={t('contracts.new.name')}
        value={form.name}
        onChange={(name) => {
          patch({ name });
        }}
        placeholder={t('contracts.new.namePlaceholder')}
      />

      <div className="lq-sect">
        <h3 className="lq-sect__title">{t('contracts.new.parties')}</h3>
      </div>
      {/* Boxed together, because they are one decision. This is the moment somebody chooses who will
          be asked to sign, and two fields floating in a column of fields does not read that way. */}
      <div className="lq-ct__parties">
        <div className="lq-ct__partyPair">
          <TextField
            label={t('contracts.new.producer')}
            value={form.producer}
            onChange={(producer) => {
              patch({ producer });
            }}
            placeholder={t('contracts.new.producerPlaceholder')}
          />
          <TextField
            label={t('contracts.new.consumer')}
            value={form.consumer}
            onChange={(consumer) => {
              patch({ consumer });
            }}
            placeholder={t('contracts.new.consumerPlaceholder')}
          />
        </div>
        <p className="lq-ct__partyNote">{t('contracts.new.partiesNote')}</p>
        {sameParty && (
          <p className="lq-ct__partyError">{t('contracts.new.samePartyError')}</p>
        )}
      </div>

      <div className="lq-sect">
        <h3 className="lq-sect__title">{t('contracts.new.terms')}</h3>
      </div>
      {TERM_KINDS.map((kind) => {
        const field = TERM_FIELD[kind];
        const on = field.on(form);
        return (
          <div key={kind} className="lq-ct__termBox">
            <div className="lq-ct__termHead">
              <CheckboxField
                label={t(`contracts.term.${kind}`)}
                checked={on}
                onChange={(checked) => {
                  patch(field.toggle(checked));
                }}
              />
            </div>
            {on && (
              <div className="lq-ct__termBody">
                <div className="lq-ct__termInput">
                  <TextField
                    label={t(`contracts.new.value.${kind}`)}
                    type="number"
                    value={field.value(form)}
                    onChange={(value) => {
                      patch(field.set(value));
                    }}
                    placeholder={t(`contracts.new.placeholder.${kind}`)}
                  />
                  {kind === 'assessment' && (
                    <SelectField<AgeUnit>
                      label={t('contracts.new.unit')}
                      value={form.ageUnit}
                      onChange={(ageUnit) => {
                        patch({ ageUnit });
                      }}
                      options={[
                        { value: 'hours', label: t('contracts.new.hours') },
                        { value: 'days', label: t('contracts.new.days') },
                      ]}
                    />
                  )}
                </div>
                {/* The absence rule, where the term is chosen. */}
                <p className="lq-ct__termWhy">{t(`contracts.new.why.${kind}`)}</p>
              </div>
            )}
          </div>
        );
      })}

      <div className="lq-sect">
        <h3 className="lq-sect__title">{t('contracts.new.severity')}</h3>
      </div>
      <div
        className="lq-ct__sevs"
        role="radiogroup"
        aria-label={t('contracts.new.severity')}
      >
        {SEVERITIES.map((severity) => (
          <label
            key={severity}
            className={
              form.severity === severity ? 'lq-ct__sev lq-ct__sev--on' : 'lq-ct__sev'
            }
          >
            <input
              type="radio"
              name="contract-severity"
              value={severity}
              checked={form.severity === severity}
              onChange={() => {
                patch({ severity });
              }}
            />
            {t(`contracts.severity.${severity}`)}
          </label>
        ))}
      </div>
      <p className="lq-ct__sevWhy">{t('contracts.new.severityWhy')}</p>

      {problem !== null && (
        <Alert tone="error" title={t('contracts.new.notReady')}>
          {t(problem)}
        </Alert>
      )}
      {create.isError && (
        <Alert tone="error" title={t('contracts.new.refused')}>
          {create.error.message}
        </Alert>
      )}

      {/* The freeze statement sits where somebody is about to act, not in the lede they have scrolled
          past. Creating does *not* freeze anything — submitting does — and saying so here is what
          makes the draft stage feel like the cheap place to be careful. */}
      <p className="lq-ct__freeze">{t('contracts.new.freeze')}</p>

      <div className="lq-ct__newActions">
        <button type="button" className="lq-ct__act" onClick={onClose}>
          {t('contracts.new.cancel')}
        </button>
        <button
          type="button"
          className="lq-ct__act lq-ct__act--primary lq-ct__newSubmit"
          disabled={create.isPending}
          onClick={submit}
        >
          {t('contracts.new.create')}
        </button>
      </div>
    </Drawer>
  );
}
